import React, { useState, useEffect, useCallback } from 'react';
import {
  RiskCard, RiskCardHeader, RiskCardBody,
  RiskButton, RiskBadge, RiskProgress
} from '../../components/risk/UiKit';

const API_BASE = process.env.REACT_APP_API_URL + '/api';

const SURVEY_TYPES = [
  { value: 'compliance', label: 'Compliance' },
  { value: 'risk_assessment', label: 'Risk Assessment' },
  { value: 'monitoring', label: 'Monitoring' },
  { value: 'custom', label: 'Custom' }
];

const QUESTION_TYPES = [
  { value: 'text', label: 'Free Text' },
  { value: 'yes_no', label: 'Yes / No' },
  { value: 'scale_1_5', label: 'Scale 1-5' },
  { value: 'scale_1_10', label: 'Scale 1-10' },
  { value: 'select', label: 'Single Select' },
  { value: 'multi_select', label: 'Multi Select' }
];

const STATUS_COLORS = {
  draft: 'slate',
  active: 'green',
  closed: 'amber',
  archived: 'red'
};

function getSessionUser() {
  try { return JSON.parse(localStorage.getItem('sessionUser') || 'null'); } catch { return null; }
}

// =====================================================
// Main Component
// =====================================================

export default function SimpleSurvey() {
  const [view, setView] = useState('list');           // list | editor | respond | results
  const [surveys, setSurveys] = useState([]);
  const [selectedSurvey, setSelectedSurvey] = useState(null);
  const [isCompliance, setIsCompliance] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchSurveys = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/simple-surveys`, { credentials: 'include' });
      const data = await res.json();
      if (data.success) {
        setSurveys(data.surveys);
        setIsCompliance(data.isCompliance);
      } else {
        setError(data.message || 'Failed to load surveys');
      }
    } catch (err) {
      setError('Network error loading surveys');
      console.error(err);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchSurveys(); }, [fetchSurveys]);

  const handleSelectSurvey = (survey) => {
    setSelectedSurvey(survey);
    if (isCompliance) {
      setView('editor');
    } else {
      setView(survey.status === 'active' && !survey.has_responded ? 'respond' : 'results');
    }
  };

  const handleBack = () => {
    setView('list');
    setSelectedSurvey(null);
    fetchSurveys();
  };

  const handleCreate = () => {
    setSelectedSurvey(null);
    setView('editor');
  };

  if (view === 'editor') {
    return <SurveyEditor survey={selectedSurvey} onBack={handleBack} />;
  }
  if (view === 'respond') {
    return <SurveyResponder surveyId={selectedSurvey.id} onBack={handleBack} />;
  }
  if (view === 'results' && !isCompliance) {
    return <RespondentResultView surveyId={selectedSurvey.id} onBack={handleBack} />;
  }

  // ---- List View ----
  return (
    <div className="space-y-6">
      <RiskCard>
        <RiskCardHeader
          title="Surveys"
          subtitle="Compliance surveys, risk assessments, and monitoring questionnaires"
          actions={
            isCompliance && (
              <RiskButton onClick={handleCreate}>+ New Survey</RiskButton>
            )
          }
        />
        <RiskCardBody>
          {loading && <p className="text-slate-500 text-sm">Loading surveys...</p>}
          {error && <p className="text-red-600 text-sm">{error}</p>}
          {!loading && surveys.length === 0 && (
            <p className="text-slate-400 text-sm py-8 text-center">No surveys found.</p>
          )}
          <div className="space-y-3">
            {surveys.map(s => (
              <div
                key={s.id}
                onClick={() => handleSelectSurvey(s)}
                className="flex items-center justify-between p-4 rounded-xl border border-slate-200 hover:border-blue-300 hover:shadow-sm cursor-pointer transition-all"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-slate-900 truncate">{s.title}</span>
                    <RiskBadge color={STATUS_COLORS[s.status] || 'slate'}>{s.status}</RiskBadge>
                    <RiskBadge color="blue">{SURVEY_TYPES.find(t => t.value === s.survey_type)?.label || s.survey_type}</RiskBadge>
                  </div>
                  <p className="text-sm text-slate-500 mt-1 truncate">{s.description || 'No description'}</p>
                  <div className="flex items-center gap-4 mt-1 text-xs text-slate-400">
                    {s.question_count !== undefined && <span>{s.question_count} questions</span>}
                    {s.response_count !== undefined && <span>{s.response_count} respondents</span>}
                    {s.due_date && <span>Due: {new Date(s.due_date).toLocaleDateString()}</span>}
                    {s.has_responded && <span className="text-green-600 font-medium">Completed</span>}
                  </div>
                </div>
                <svg className="w-5 h-5 text-slate-400 flex-shrink-0 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            ))}
          </div>
        </RiskCardBody>
      </RiskCard>
    </div>
  );
}

// =====================================================
// Survey Editor (compliance only)
// =====================================================

function SurveyEditor({ survey: initialSurvey, onBack }) {
  const [survey, setSurvey] = useState(initialSurvey);
  const [questions, setQuestions] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  // Metadata form
  const [title, setTitle] = useState(initialSurvey?.title || '');
  const [description, setDescription] = useState(initialSurvey?.description || '');
  const [surveyType, setSurveyType] = useState(initialSurvey?.survey_type || 'compliance');
  const [targetDepts, setTargetDepts] = useState(initialSurvey?.target_departments || []);
  const [targetRoles, setTargetRoles] = useState(initialSurvey?.target_roles || []);
  const [dueDate, setDueDate] = useState(initialSurvey?.due_date?.slice(0, 10) || '');
  const [anonymous, setAnonymous] = useState(initialSurvey?.anonymous || false);

  // New question form
  const [newQText, setNewQText] = useState('');
  const [newQType, setNewQType] = useState('text');
  const [newQRequired, setNewQRequired] = useState(true);
  const [newQOptions, setNewQOptions] = useState('');
  const [newQHelp, setNewQHelp] = useState('');

  // Fetch departments for targeting
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/departments`, { credentials: 'include' });
        const data = await res.json();
        if (data.success) setDepartments(data.departments || []);
      } catch (_) {}
    })();
  }, []);

  // If editing existing survey, fetch questions
  useEffect(() => {
    if (survey?.id) fetchSurveyDetail(survey.id);
  }, [survey?.id]);

  const fetchSurveyDetail = async (id) => {
    try {
      const res = await fetch(`${API_BASE}/simple-surveys/${id}`, { credentials: 'include' });
      const data = await res.json();
      if (data.success) {
        setSurvey(data.survey);
        setQuestions(data.questions || []);
        setTitle(data.survey.title);
        setDescription(data.survey.description || '');
        setSurveyType(data.survey.survey_type);
        setTargetDepts(data.survey.target_departments || []);
        setTargetRoles(data.survey.target_roles || []);
        setDueDate(data.survey.due_date?.slice(0, 10) || '');
        setAnonymous(data.survey.anonymous || false);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const flashMessage = (msg) => {
    setMessage(msg);
    setTimeout(() => setMessage(''), 4000);
  };

  // Save or create survey metadata
  const handleSaveMetadata = async () => {
    if (!title.trim()) { flashMessage('Title is required'); return; }
    setSaving(true);
    try {
      const payload = {
        title, description, survey_type: surveyType,
        target_departments: targetDepts,
        target_roles: targetRoles,
        due_date: dueDate || null,
        anonymous
      };
      const url = survey?.id ? `${API_BASE}/simple-surveys/${survey.id}` : `${API_BASE}/simple-surveys`;
      const method = survey?.id ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method, credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (data.success) {
        setSurvey(data.survey);
        flashMessage(survey?.id ? 'Saved' : 'Survey created');
      } else {
        flashMessage(data.message || 'Save failed');
      }
    } catch (err) {
      flashMessage('Network error');
      console.error(err);
    }
    setSaving(false);
  };

  // Add a question
  const handleAddQuestion = async () => {
    if (!survey?.id) { flashMessage('Save the survey first'); return; }
    if (!newQText.trim()) { flashMessage('Question text is required'); return; }

    const needsOptions = ['select', 'multi_select'].includes(newQType);
    const optionsArr = needsOptions
      ? newQOptions.split(',').map(o => o.trim()).filter(Boolean)
      : null;
    if (needsOptions && (!optionsArr || optionsArr.length < 2)) {
      flashMessage('Provide at least 2 comma-separated options');
      return;
    }

    try {
      const res = await fetch(`${API_BASE}/simple-surveys/${survey.id}/questions`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          questions: [{
            question_text: newQText.trim(),
            question_type: newQType,
            required: newQRequired,
            options: optionsArr,
            help_text: newQHelp.trim() || null
          }]
        })
      });
      const data = await res.json();
      if (data.success) {
        setQuestions(prev => [...prev, ...data.questions]);
        setNewQText('');
        setNewQOptions('');
        setNewQHelp('');
      } else {
        flashMessage(data.message || 'Failed to add question');
      }
    } catch (err) {
      flashMessage('Network error');
      console.error(err);
    }
  };

  // Delete a question
  const handleDeleteQuestion = async (qid) => {
    try {
      const res = await fetch(`${API_BASE}/simple-surveys/${survey.id}/questions/${qid}`, {
        method: 'DELETE', credentials: 'include'
      });
      const data = await res.json();
      if (data.success) {
        setQuestions(prev => prev.filter(q => q.id !== qid));
      } else {
        flashMessage(data.message || 'Failed to delete');
      }
    } catch (err) {
      flashMessage('Network error');
    }
  };

  // Move question up/down
  const handleMoveQuestion = async (index, direction) => {
    const newQuestions = [...questions];
    const swapIdx = direction === 'up' ? index - 1 : index + 1;
    if (swapIdx < 0 || swapIdx >= newQuestions.length) return;

    [newQuestions[index], newQuestions[swapIdx]] = [newQuestions[swapIdx], newQuestions[index]];
    const order = newQuestions.map((q, i) => ({ id: q.id, display_order: i }));

    // Optimistic UI update
    setQuestions(newQuestions);

    try {
      const res = await fetch(`${API_BASE}/simple-surveys/${survey.id}/questions/reorder`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ order })
      });
      const data = await res.json();
      if (data.success) {
        setQuestions(data.questions);
      }
    } catch (err) {
      console.error(err);
    }
  };

  // Deploy
  const handleDeploy = async () => {
    if (!window.confirm('Deploy this survey? It will become visible to targeted users.')) return;
    try {
      const res = await fetch(`${API_BASE}/simple-surveys/${survey.id}/deploy`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ notify: true })
      });
      const data = await res.json();
      if (data.success) {
        setSurvey(prev => ({ ...prev, status: 'active' }));
        flashMessage(`Deployed. ${data.notifiedCount || 0} users notified.`);
      } else {
        flashMessage(data.message || 'Deploy failed');
      }
    } catch (err) {
      flashMessage('Network error');
    }
  };

  // Close
  const handleClose = async () => {
    if (!window.confirm('Close this survey? No more responses will be accepted.')) return;
    try {
      const res = await fetch(`${API_BASE}/simple-surveys/${survey.id}/close`, {
        method: 'POST', credentials: 'include'
      });
      const data = await res.json();
      if (data.success) {
        setSurvey(prev => ({ ...prev, status: 'closed' }));
        flashMessage('Survey closed');
      } else {
        flashMessage(data.message || 'Close failed');
      }
    } catch (err) {
      flashMessage('Network error');
    }
  };

  // View results
  const [showResults, setShowResults] = useState(false);

  const isDraft = survey?.status === 'draft';
  const isActive = survey?.status === 'active';

  // Toggle department in target list
  const toggleDept = (deptId) => {
    setTargetDepts(prev =>
      prev.includes(deptId) ? prev.filter(d => d !== deptId) : [...prev, deptId]
    );
  };

  if (showResults && survey?.id) {
    return <SurveyResults surveyId={survey.id} onBack={() => setShowResults(false)} />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <button onClick={onBack} className="text-slate-500 hover:text-slate-700 text-sm font-medium flex items-center gap-1">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
          Back to Surveys
        </button>
        <div className="flex items-center gap-2">
          {survey && <RiskBadge color={STATUS_COLORS[survey.status] || 'slate'}>{survey.status}</RiskBadge>}
          {(isActive || survey?.status === 'closed') && (
            <RiskButton variant="outline" onClick={() => setShowResults(true)}>View Results</RiskButton>
          )}
          {isDraft && questions.length > 0 && (
            <RiskButton onClick={handleDeploy}>Deploy Survey</RiskButton>
          )}
          {isActive && (
            <RiskButton variant="outline" onClick={handleClose}>Close Survey</RiskButton>
          )}
        </div>
      </div>

      {message && (
        <div className="px-4 py-2 rounded-xl bg-blue-50 border border-blue-200 text-blue-700 text-sm">
          {message}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left: Metadata */}
        <RiskCard>
          <RiskCardHeader title={survey?.id ? 'Edit Survey' : 'New Survey'} />
          <RiskCardBody className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Title</label>
              <input
                type="text"
                value={title}
                onChange={e => setTitle(e.target.value)}
                className="w-full border border-slate-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Survey title"
                disabled={!isDraft && survey?.id}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
              <textarea
                value={description}
                onChange={e => setDescription(e.target.value)}
                rows={3}
                className="w-full border border-slate-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                placeholder="Describe the purpose of this survey"
                disabled={!isDraft && survey?.id}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Type</label>
                <select
                  value={surveyType}
                  onChange={e => setSurveyType(e.target.value)}
                  className="w-full border border-slate-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={!isDraft && survey?.id}
                >
                  {SURVEY_TYPES.map(t => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Due Date</label>
                <input
                  type="date"
                  value={dueDate}
                  onChange={e => setDueDate(e.target.value)}
                  className="w-full border border-slate-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Target Departments</label>
              <div className="flex flex-wrap gap-2 mt-1">
                {departments.length === 0 && <span className="text-xs text-slate-400">No departments loaded</span>}
                {departments.map(d => (
                  <button
                    key={d.id}
                    onClick={() => toggleDept(d.id)}
                    className={`px-3 py-1 rounded-full text-xs border transition-all ${
                      targetDepts.includes(d.id)
                        ? 'bg-blue-50 border-blue-300 text-blue-700'
                        : 'bg-white border-slate-300 text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    {d.name}
                  </button>
                ))}
              </div>
              {targetDepts.length === 0 && <p className="text-xs text-slate-400 mt-1">All departments (no filter)</p>}
            </div>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={anonymous}
                  onChange={e => setAnonymous(e.target.checked)}
                  className="rounded border-slate-300"
                />
                Anonymous responses
              </label>
            </div>
            <div className="pt-2">
              <RiskButton onClick={handleSaveMetadata} disabled={saving}>
                {saving ? 'Saving...' : survey?.id ? 'Save Changes' : 'Create Survey'}
              </RiskButton>
            </div>
          </RiskCardBody>
        </RiskCard>

        {/* Right: Questions */}
        <RiskCard>
          <RiskCardHeader
            title="Questions"
            subtitle={survey?.id ? `${questions.length} question${questions.length !== 1 ? 's' : ''}` : 'Save the survey first to add questions'}
          />
          <RiskCardBody className="space-y-4">
            {/* Question list */}
            {questions.length > 0 && (
              <div className="space-y-2">
                {questions.map((q, idx) => (
                  <div key={q.id} className="flex items-start gap-2 p-3 rounded-xl border border-slate-200 bg-slate-50">
                    <span className="text-xs font-bold text-slate-400 mt-1 w-5 text-center flex-shrink-0">{idx + 1}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-800">{q.question_text}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <RiskBadge color="blue">
                          {QUESTION_TYPES.find(t => t.value === q.question_type)?.label || q.question_type}
                        </RiskBadge>
                        {q.required && <RiskBadge color="amber">Required</RiskBadge>}
                        {q.help_text && (
                          <span className="text-xs text-slate-400" title={q.help_text}>Has help text</span>
                        )}
                      </div>
                      {q.options && Array.isArray(q.options) && (
                        <p className="text-xs text-slate-400 mt-1">Options: {q.options.join(', ')}</p>
                      )}
                    </div>
                    {isDraft && (
                      <div className="flex flex-col gap-1 flex-shrink-0">
                        <button
                          onClick={() => handleMoveQuestion(idx, 'up')}
                          disabled={idx === 0}
                          className="text-slate-400 hover:text-slate-600 disabled:opacity-30"
                          title="Move up"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" /></svg>
                        </button>
                        <button
                          onClick={() => handleMoveQuestion(idx, 'down')}
                          disabled={idx === questions.length - 1}
                          className="text-slate-400 hover:text-slate-600 disabled:opacity-30"
                          title="Move down"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                        </button>
                        <button
                          onClick={() => handleDeleteQuestion(q.id)}
                          className="text-red-400 hover:text-red-600 mt-1"
                          title="Delete"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Inline add question form (draft only) */}
            {isDraft && survey?.id && (
              <div className="border border-dashed border-slate-300 rounded-xl p-4 space-y-3">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Add Question</p>
                <input
                  type="text"
                  value={newQText}
                  onChange={e => setNewQText(e.target.value)}
                  placeholder="Question text"
                  className="w-full border border-slate-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <div className="grid grid-cols-2 gap-3">
                  <select
                    value={newQType}
                    onChange={e => setNewQType(e.target.value)}
                    className="border border-slate-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {QUESTION_TYPES.map(t => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                  <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={newQRequired}
                      onChange={e => setNewQRequired(e.target.checked)}
                      className="rounded border-slate-300"
                    />
                    Required
                  </label>
                </div>
                {['select', 'multi_select'].includes(newQType) && (
                  <input
                    type="text"
                    value={newQOptions}
                    onChange={e => setNewQOptions(e.target.value)}
                    placeholder="Comma-separated options (e.g. Yes, No, N/A)"
                    className="w-full border border-slate-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                )}
                <input
                  type="text"
                  value={newQHelp}
                  onChange={e => setNewQHelp(e.target.value)}
                  placeholder="Help text (optional)"
                  className="w-full border border-slate-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <RiskButton onClick={handleAddQuestion}>Add Question</RiskButton>
              </div>
            )}

            {!survey?.id && (
              <p className="text-sm text-slate-400 text-center py-4">
                Create the survey to start adding questions.
              </p>
            )}
          </RiskCardBody>
        </RiskCard>
      </div>
    </div>
  );
}

// =====================================================
// Survey Responder (card-by-card with progress)
// =====================================================

function SurveyResponder({ surveyId, onBack }) {
  const [survey, setSurvey] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});   // { questionId: value }
  const [currentIdx, setCurrentIdx] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API_BASE}/simple-surveys/${surveyId}`, { credentials: 'include' });
        const data = await res.json();
        if (data.success) {
          setSurvey(data.survey);
          setQuestions(data.questions || []);
          // Pre-fill with existing responses if any
          const existing = {};
          (data.myResponses || []).forEach(r => { existing[r.question_id] = r.response_value; });
          setAnswers(existing);
        } else {
          setError(data.message || 'Failed to load survey');
        }
      } catch (err) {
        setError('Network error');
      }
      setLoading(false);
    })();
  }, [surveyId]);

  const setAnswer = (qid, value) => {
    setAnswers(prev => ({ ...prev, [qid]: value }));
  };

  const handleSubmit = async () => {
    // Validate required
    for (const q of questions) {
      if (q.required && !answers[q.id] && answers[q.id] !== '0') {
        setError(`Please answer all required questions. Missing: "${q.question_text}"`);
        const missingIdx = questions.findIndex(qq => qq.id === q.id);
        if (missingIdx >= 0) setCurrentIdx(missingIdx);
        return;
      }
    }

    setSubmitting(true);
    setError('');
    try {
      const responses = questions.map(q => ({
        question_id: q.id,
        response_value: answers[q.id] !== undefined ? String(answers[q.id]) : ''
      }));
      const res = await fetch(`${API_BASE}/simple-surveys/${surveyId}/respond`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ responses })
      });
      const data = await res.json();
      if (data.success) {
        setSubmitted(true);
      } else {
        setError(data.message || 'Submission failed');
      }
    } catch (err) {
      setError('Network error');
    }
    setSubmitting(false);
  };

  if (loading) {
    return (
      <RiskCard>
        <RiskCardBody><p className="text-slate-500 text-sm py-8 text-center">Loading survey...</p></RiskCardBody>
      </RiskCard>
    );
  }

  if (submitted) {
    return (
      <div className="max-w-lg mx-auto mt-12">
        <RiskCard>
          <RiskCardBody className="text-center py-12">
            <div className="text-4xl mb-4">&#10003;</div>
            <h2 className="text-xl font-semibold text-slate-900 mb-2">Response Submitted</h2>
            <p className="text-slate-500 text-sm mb-6">Thank you for completing the survey.</p>
            <RiskButton onClick={onBack}>Back to Surveys</RiskButton>
          </RiskCardBody>
        </RiskCard>
      </div>
    );
  }

  const q = questions[currentIdx];
  const progress = questions.length > 0 ? ((currentIdx + 1) / questions.length) * 100 : 0;

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <button onClick={onBack} className="text-slate-500 hover:text-slate-700 text-sm font-medium flex items-center gap-1">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
          Back
        </button>
        <span className="text-sm text-slate-500">{currentIdx + 1} of {questions.length}</span>
      </div>

      {/* Progress bar */}
      <RiskProgress value={progress} />

      {/* Survey title */}
      <h2 className="text-lg font-semibold text-slate-900">{survey?.title}</h2>

      {error && (
        <div className="px-4 py-2 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>
      )}

      {/* Question card */}
      {q && (
        <RiskCard>
          <RiskCardBody className="space-y-4">
            <div className="flex items-start gap-2">
              <span className="text-sm font-bold text-slate-400 mt-0.5">{currentIdx + 1}.</span>
              <div>
                <p className="text-base font-medium text-slate-900">{q.question_text}</p>
                {q.help_text && <p className="text-xs text-slate-400 mt-1">{q.help_text}</p>}
                {q.required && <span className="text-xs text-amber-600 font-medium">Required</span>}
              </div>
            </div>

            <QuestionInput
              question={q}
              value={answers[q.id]}
              onChange={(val) => setAnswer(q.id, val)}
            />
          </RiskCardBody>
        </RiskCard>
      )}

      {/* Navigation */}
      <div className="flex items-center justify-between">
        <RiskButton
          variant="outline"
          onClick={() => setCurrentIdx(i => Math.max(0, i - 1))}
          disabled={currentIdx === 0}
        >
          Previous
        </RiskButton>

        {currentIdx < questions.length - 1 ? (
          <RiskButton onClick={() => setCurrentIdx(i => i + 1)}>Next</RiskButton>
        ) : (
          <RiskButton onClick={handleSubmit} disabled={submitting}>
            {submitting ? 'Submitting...' : 'Submit Survey'}
          </RiskButton>
        )}
      </div>
    </div>
  );
}

// =====================================================
// Question Input Renderer
// =====================================================

function QuestionInput({ question, value, onChange }) {
  const { question_type, options } = question;

  switch (question_type) {
    case 'text':
      return (
        <textarea
          value={value || ''}
          onChange={e => onChange(e.target.value)}
          rows={3}
          className="w-full border border-slate-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
          placeholder="Type your answer..."
        />
      );

    case 'yes_no':
      return (
        <div className="flex gap-3">
          {['Yes', 'No'].map(opt => (
            <button
              key={opt}
              onClick={() => onChange(opt)}
              className={`flex-1 py-3 rounded-xl border text-sm font-semibold transition-all ${
                value === opt
                  ? 'bg-blue-50 border-blue-300 text-blue-700'
                  : 'border-slate-300 text-slate-600 hover:bg-slate-50'
              }`}
            >
              {opt}
            </button>
          ))}
        </div>
      );

    case 'scale_1_5':
    case 'scale_1_10': {
      const max = question_type === 'scale_1_5' ? 5 : 10;
      const scaleValues = Array.from({ length: max }, (_, i) => i + 1);
      return (
        <div className="flex flex-wrap gap-2">
          {scaleValues.map(n => (
            <button
              key={n}
              onClick={() => onChange(String(n))}
              className={`w-10 h-10 rounded-xl border text-sm font-semibold transition-all ${
                String(value) === String(n)
                  ? 'bg-blue-600 border-blue-600 text-white'
                  : 'border-slate-300 text-slate-600 hover:bg-slate-50'
              }`}
            >
              {n}
            </button>
          ))}
        </div>
      );
    }

    case 'select': {
      const opts = Array.isArray(options) ? options : [];
      return (
        <div className="space-y-2">
          {opts.map(opt => (
            <button
              key={opt}
              onClick={() => onChange(opt)}
              className={`w-full text-left px-4 py-2.5 rounded-xl border text-sm transition-all ${
                value === opt
                  ? 'bg-blue-50 border-blue-300 text-blue-700 font-semibold'
                  : 'border-slate-300 text-slate-600 hover:bg-slate-50'
              }`}
            >
              {opt}
            </button>
          ))}
        </div>
      );
    }

    case 'multi_select': {
      const opts = Array.isArray(options) ? options : [];
      let selected = [];
      try { selected = JSON.parse(value || '[]'); } catch { selected = []; }
      if (!Array.isArray(selected)) selected = [];

      const toggle = (opt) => {
        const next = selected.includes(opt)
          ? selected.filter(s => s !== opt)
          : [...selected, opt];
        onChange(JSON.stringify(next));
      };

      return (
        <div className="space-y-2">
          {opts.map(opt => (
            <button
              key={opt}
              onClick={() => toggle(opt)}
              className={`w-full text-left px-4 py-2.5 rounded-xl border text-sm transition-all ${
                selected.includes(opt)
                  ? 'bg-blue-50 border-blue-300 text-blue-700 font-semibold'
                  : 'border-slate-300 text-slate-600 hover:bg-slate-50'
              }`}
            >
              <span className="flex items-center gap-2">
                <span className={`w-4 h-4 rounded border flex items-center justify-center text-xs ${
                  selected.includes(opt) ? 'bg-blue-600 border-blue-600 text-white' : 'border-slate-400'
                }`}>
                  {selected.includes(opt) && '\u2713'}
                </span>
                {opt}
              </span>
            </button>
          ))}
        </div>
      );
    }

    default:
      return (
        <input
          type="text"
          value={value || ''}
          onChange={e => onChange(e.target.value)}
          className="w-full border border-slate-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      );
  }
}

// =====================================================
// Survey Results (compliance view)
// =====================================================

function SurveyResults({ surveyId, onBack }) {
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/simple-surveys/${surveyId}/results`, { credentials: 'include' });
        const data = await res.json();
        if (data.success) {
          setResults(data);
        } else {
          setError(data.message || 'Failed to load results');
        }
      } catch (err) {
        setError('Network error');
      }
      setLoading(false);
    })();
  }, [surveyId]);

  if (loading) {
    return <RiskCard><RiskCardBody><p className="text-sm text-slate-500 py-8 text-center">Loading results...</p></RiskCardBody></RiskCard>;
  }

  if (error) {
    return (
      <div className="space-y-4">
        <button onClick={onBack} className="text-slate-500 hover:text-slate-700 text-sm font-medium flex items-center gap-1">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
          Back
        </button>
        <RiskCard><RiskCardBody><p className="text-red-600 text-sm">{error}</p></RiskCardBody></RiskCard>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <button onClick={onBack} className="text-slate-500 hover:text-slate-700 text-sm font-medium flex items-center gap-1">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
          Back
        </button>
        <span className="text-sm text-slate-500">{results.uniqueRespondents} respondent{results.uniqueRespondents !== 1 ? 's' : ''}</span>
      </div>

      <RiskCard>
        <RiskCardHeader title={`Results: ${results.survey.title}`} />
        <RiskCardBody className="space-y-6">
          {results.results.map((r, idx) => (
            <div key={r.question_id} className="border border-slate-200 rounded-xl p-4">
              <p className="text-sm font-semibold text-slate-800 mb-2">
                <span className="text-slate-400 mr-1">{idx + 1}.</span>
                {r.question_text}
              </p>
              <RiskBadge color="blue">
                {QUESTION_TYPES.find(t => t.value === r.question_type)?.label || r.question_type}
              </RiskBadge>

              <div className="mt-3">
                <ResultVisualization result={r} />
              </div>

              <p className="text-xs text-slate-400 mt-2">{r.total} response{r.total !== 1 ? 's' : ''}</p>
            </div>
          ))}
        </RiskCardBody>
      </RiskCard>
    </div>
  );
}

// =====================================================
// Result Visualization per question type
// =====================================================

function ResultVisualization({ result }) {
  const { question_type, counts, average, distribution, responses, min, max, total } = result;

  if (question_type === 'yes_no' && counts) {
    const totalCount = (counts.yes || 0) + (counts.no || 0);
    return (
      <div className="space-y-2">
        {['yes', 'no'].map(key => {
          const count = counts[key] || 0;
          const pct = totalCount > 0 ? Math.round((count / totalCount) * 100) : 0;
          return (
            <div key={key} className="flex items-center gap-3">
              <span className="text-sm font-medium text-slate-600 w-8 capitalize">{key}</span>
              <div className="flex-1 bg-slate-100 rounded-full h-5 overflow-hidden">
                <div
                  className={`h-full rounded-full ${key === 'yes' ? 'bg-green-500' : 'bg-red-400'}`}
                  style={{ width: `${pct}%` }}
                />
              </div>
              <span className="text-xs text-slate-500 w-16 text-right">{count} ({pct}%)</span>
            </div>
          );
        })}
      </div>
    );
  }

  if ((question_type === 'scale_1_5' || question_type === 'scale_1_10') && average !== null && average !== undefined) {
    const scaleMax = question_type === 'scale_1_5' ? 5 : 10;
    return (
      <div className="space-y-2">
        <div className="flex items-center gap-4">
          <span className="text-3xl font-bold text-blue-600">{average}</span>
          <span className="text-sm text-slate-500">/ {scaleMax} average</span>
          <span className="text-xs text-slate-400">(min: {min}, max: {max})</span>
        </div>
        {distribution && (
          <div className="flex items-end gap-1 h-16">
            {Array.from({ length: scaleMax }, (_, i) => i + 1).map(n => {
              const count = distribution[n] || 0;
              const maxCount = Math.max(1, ...Object.values(distribution).map(Number));
              const height = (count / maxCount) * 100;
              return (
                <div key={n} className="flex flex-col items-center flex-1">
                  <div className="w-full bg-blue-200 rounded-t" style={{ height: `${Math.max(height, 2)}%` }} />
                  <span className="text-xs text-slate-500 mt-1">{n}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  if ((question_type === 'select' || question_type === 'multi_select') && counts) {
    const entries = Object.entries(counts).sort((a, b) => b[1] - a[1]);
    const maxCount = Math.max(1, ...entries.map(e => e[1]));
    return (
      <div className="space-y-2">
        {entries.map(([label, count]) => {
          const pct = total > 0 ? Math.round((count / total) * 100) : 0;
          return (
            <div key={label} className="flex items-center gap-3">
              <span className="text-sm text-slate-600 w-32 truncate" title={label}>{label}</span>
              <div className="flex-1 bg-slate-100 rounded-full h-5 overflow-hidden">
                <div
                  className="h-full bg-blue-500 rounded-full"
                  style={{ width: `${(count / maxCount) * 100}%` }}
                />
              </div>
              <span className="text-xs text-slate-500 w-16 text-right">{count} ({pct}%)</span>
            </div>
          );
        })}
      </div>
    );
  }

  if (question_type === 'text' && responses) {
    return (
      <div className="space-y-2 max-h-48 overflow-y-auto">
        {responses.length === 0 && <p className="text-sm text-slate-400">No responses yet.</p>}
        {responses.map((r, i) => (
          <div key={i} className="text-sm bg-slate-50 rounded-xl px-3 py-2 border border-slate-200">
            <p className="text-slate-700">{r.value || '(empty)'}</p>
            {r.department_name && <p className="text-xs text-slate-400 mt-1">{r.department_name}</p>}
          </div>
        ))}
      </div>
    );
  }

  return <p className="text-sm text-slate-400">No data available.</p>;
}

// =====================================================
// Respondent result view (non-compliance, after completing)
// =====================================================

function RespondentResultView({ surveyId, onBack }) {
  const [survey, setSurvey] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [myResponses, setMyResponses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/simple-surveys/${surveyId}`, { credentials: 'include' });
        const data = await res.json();
        if (data.success) {
          setSurvey(data.survey);
          setQuestions(data.questions || []);
          setMyResponses(data.myResponses || []);
        }
      } catch (_) {}
      setLoading(false);
    })();
  }, [surveyId]);

  if (loading) {
    return <RiskCard><RiskCardBody><p className="text-sm text-slate-500 py-8 text-center">Loading...</p></RiskCardBody></RiskCard>;
  }

  const responseMap = new Map(myResponses.map(r => [r.question_id, r.response_value]));

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <button onClick={onBack} className="text-slate-500 hover:text-slate-700 text-sm font-medium flex items-center gap-1">
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
        Back to Surveys
      </button>

      <RiskCard>
        <RiskCardHeader
          title={survey?.title || 'Survey'}
          subtitle={survey?.status === 'closed' ? 'This survey is now closed' : 'Your submitted responses'}
        />
        <RiskCardBody className="space-y-4">
          {questions.map((q, idx) => (
            <div key={q.id} className="border border-slate-200 rounded-xl p-4">
              <p className="text-sm font-medium text-slate-800">
                <span className="text-slate-400 mr-1">{idx + 1}.</span>
                {q.question_text}
              </p>
              <p className="text-sm text-blue-700 mt-2 bg-blue-50 rounded-lg px-3 py-2">
                {formatResponseValue(q, responseMap.get(q.id))}
              </p>
            </div>
          ))}
        </RiskCardBody>
      </RiskCard>
    </div>
  );
}

function formatResponseValue(question, value) {
  if (value === undefined || value === null || value === '') return '(no response)';
  if (question.question_type === 'multi_select') {
    try {
      const arr = JSON.parse(value);
      if (Array.isArray(arr)) return arr.join(', ');
    } catch { /* fall through */ }
  }
  return value;
}
